/**
 * Real PWI TTP + registration + lesion-segmentation sequence, factored out
 * the same way `dwi-pipeline.ts` is, so both the main pipeline runner
 * (`wasm-engine/engine.ts`) and any future standalone Tools entry call the
 * exact same, already-validated logic instead of two drifting copies.
 *
 * Ported verbatim (not re-derived) from `openads-wasm/app/src/main.ts`'s
 * `runTtpStage()`/`runPwiRegistrationStage()`/`runLesionSeg()` PWI branch,
 * itself reconfirmed against the real OpenADS `registration_pwi_align.py` /
 * `preprocessing_pwi_genttp.py` / `models/dagmnet_pwi.py`, and carrying its
 * own validation number (Dice=0.7143 vs bundled ground truth, HPsymclassic
 * channel zero-filled — see runPwiSegmentation's own comment for why that is
 * load-bearing, not an oversight).
 *
 * PWI's own registration is NOT self-contained: `registration_pwi_align.py`
 * reuses DWI's native brain mask and DWI's own native->MNI affine transform
 * (both modalities land in the same DWI-native-derived MNI space so core and
 * hypoperfusion can later be compared). Every function below that needs this
 * takes DWI's already-computed prep/registration result as a plain
 * parameter — the caller (`engine.ts`) is responsible for having run (or
 * reused) DWI's skull-strip + registration stage first, same as the
 * reference script required switching to DWI and running it first.
 */
import { runAntsRegistration, runAntsApplyTransform } from "./ants-registration-client";
import { runWorker, ab } from "./worker-client";
import { percentileClipRescaleInMask, zscoreInMask, type Dims } from "./volume";
import type { NiftiVolume, NiftiVolume4D } from "./nifti";
import { extractFrame3D } from "./nifti";
import { runTtp } from "./ttp-client";
import type { Dims3, Dims4 } from "./ttp-math";
import type { DwiRegistrationResult } from "./dwi-pipeline";

import { appPath } from "./assets";
// ------------------------------------------------------------- slice timing ---

/** Real *_PWI.json SliceTiming for the bundled demo subject sub-02e8eb42 (20
 *  slices) — no separate sidecar file ships with the demo data, so these real
 *  values (transcribed from the real sidecar, per `main.ts`'s own history)
 *  are the fallback when no sidecar is found and the subject id matches. */
const DEMO_SUBJECT_ID = "02e8eb42";
const DEMO_PWI_SLICE_TIMING = [
  0.67969, 0, 0.74219, 0.07031, 0.8125, 0.14063, 0.88281, 0.20313, 0.94531, 0.27344, 1.01563, 0.34375, 1.07813,
  0.40625, 1.14844, 0.47656, 1.21875, 0.53906, 1.28125, 0.60938,
];

export interface PwiJsonSidecar {
  RepetitionTime?: number;
  SliceTiming?: number[];
}

/**
 * Real OpenADS `determine_slice_timing`/`default_slice_timing`
 * (preprocessing_pwi_genttp.py) read a `*_PWI.json` BIDS sidecar for
 * `SliceTiming` when present, falling back to a generated interleaved
 * schedule otherwise. We read a real sidecar when the caller finds one.
 * The generated-default path below is a simplified stand-in (plain
 * ascending, not the real function's interleaved acquisition-order
 * permutation) — reasonable for an unknown subject with no sidecar, but not
 * a byte-for-byte port of that fallback; the demo subject's real values
 * above are the validated path.
 */
function resolveSliceTiming(subjectId: string, nSlicesZ: number, tr: number, sidecar: PwiJsonSidecar | null): number[] {
  if (sidecar?.SliceTiming && new Set(sidecar.SliceTiming).size === sidecar.SliceTiming.length) {
    return sidecar.SliceTiming;
  }
  if (subjectId.includes(DEMO_SUBJECT_ID) && nSlicesZ === DEMO_PWI_SLICE_TIMING.length) {
    return DEMO_PWI_SLICE_TIMING;
  }
  const step = tr / nSlicesZ;
  return Array.from({ length: nSlicesZ }, (_, i) => i * step);
}

function resolveRepetitionTime(pwi4d: NiftiVolume4D, sidecar: PwiJsonSidecar | null): number {
  if (sidecar?.RepetitionTime && sidecar.RepetitionTime > 0) return sidecar.RepetitionTime;
  if (pwi4d.pixdim[3] > 0) return pwi4d.pixdim[3];
  return 1.7; // real preprocessing_pwi_genttp.py's own documented default
}

// ---------------------------------------------------------- motion correction ---

/**
 * Real per-frame PWI motion correction, ported from
 * `preprocessing_pwi_genttp.py`'s `motion_correction()`. Frame 0 is the fixed
 * reference and is left untouched; every other frame i=1..N-1 is registered
 * to it with a real `ants.registration(..., type_of_transform="Affine",
 * aff_iterations=(1000,500,250,100), metric="mattes", metric_radius=32)` call
 * (CLI captured directly from a real antspyx run, same method as the
 * affine/syn steps in `ants-registration.worker.ts` — see that file's "moco"
 * buildArgs branch for the full citation) and the resulting transform is
 * applied back onto the frame (linear interpolation — these are signal
 * intensity channels, not masks). The real pipeline's own
 * `PWIPreprocessingConfig.motion_correction['registration_params']` dict
 * (`mode: 'rigid'`, a different iteration schedule) is a red herring: it's
 * accepted by `motion_correction(img, **reg_params)` but never actually
 * forwarded to the real `ants.registration()` call inside it, which
 * hardcodes Affine with its own iteration counts instead — reproduced here
 * faithfully, not "fixed", since matching what the real code actually runs
 * is the point.
 *
 * PARALLELISM: each frame registers independently against the same fixed
 * frame-0 reference (no cross-frame dependency), and `runAntsRegistration`
 * already creates and terminates its own dedicated Worker per call — unlike
 * DAGMNet's sliding-window tasks, this didn't need a new pooled-worker
 * message protocol, just bounded concurrent dispatch instead of a serial
 * `for` loop. Originally serial: ~6.2s/frame x 59 frames = ~366s, confirmed
 * live before this change. Cap at 8 concurrent, not `hardwareConcurrency`
 * unchecked: `antsRegistration.wasm` is ~16.9MB per Worker instance (each
 * gets its own wasm32 linear memory, not shared), so 8 concurrent is
 * ~135MB of wasm binaries alone plus each registration's own runtime heap
 * for a small (PWI-native-grid-sized) volume — comfortably inside normal
 * browser budgets while leaving headroom for the main thread and whatever
 * else is running, on machines with far fewer than `hardwareConcurrency`
 * cores actually free for this.
 */
const MOCO_POOL_SIZE = 8;

async function motionCorrectPwi4d(pwi4d: NiftiVolume4D, onProgress: (stage: string) => void): Promise<NiftiVolume4D> {
  const [nx, ny, nz, nt] = pwi4d.dims;
  const spatialN = nx * ny * nz;
  const corrected = new Float32Array(pwi4d.voxels.length);
  const frame0 = extractFrame3D(pwi4d, 0);
  corrected.set(frame0.voxels, 0); // frame 0 is the fixed reference, untouched

  const fixedOverride = { voxels: frame0.voxels, dims: frame0.dims, pixdim: frame0.pixdim, affine: frame0.affine };
  const frameIndices = Array.from({ length: nt - 1 }, (_, i) => i + 1);
  let nextIdx = 0;
  let doneCount = 0;

  async function worker(): Promise<void> {
    for (;;) {
      const myIdx = nextIdx++;
      if (myIdx >= frameIndices.length) return;
      const t = frameIndices[myIdx];
      const frame = extractFrame3D(pwi4d, t);
      const reg = await runAntsRegistration(
        { step: "moco", voxels: frame.voxels, dims: frame.dims, pixdim: frame.pixdim, affine: frame.affine, fixedOverride },
        (s) => onProgress(`  moco(frame ${t}): ${s}`),
      );
      corrected.set(reg.registered, t * spatialN);
      doneCount++;
      onProgress(`motion correction: ${doneCount}/${frameIndices.length} frames done`);
    }
  }

  const poolSize = Math.max(1, Math.min(MOCO_POOL_SIZE, frameIndices.length));
  await Promise.all(Array.from({ length: poolSize }, () => worker()));

  return { ...pwi4d, voxels: corrected };
}

// ------------------------------------------------------------------- TTP ---

export interface PwiTtpResult {
  ttpMap: Float32Array;
  ttpDims: Dims3;
  ttpAffine: Float32Array;
  /** [dx,dy,dz] only — TR is not part of the 3D TTP map's own pixdim. */
  ttpPixdim: Dims;
  /** PWI series' own native-space brain mask (SynthStrip on frame 0 — a
   *  separate call from DWI's, different grid). */
  pwiMask: Uint8Array;
  pwiMaskRef: NiftiVolume;
  totalMs: number;
}

/** Real TTP generation: slice-time correction -> motion correction -> Gd
 *  concentration -> per-voxel peak detection, matching `runTtpStage()`.
 *
 *  Real pipeline order (`preprocessing_pwi_genttp.py`'s `preprocessing()`,
 *  confirmed by direct read): slice-time correction runs FIRST (on the raw
 *  series), motion correction runs SECOND (on the slice-time-corrected
 *  series), and the brain mask is derived from the temporal MEAN of the
 *  motion-corrected series, not from frame 0 alone. This function applies
 *  motion correction to the raw series BEFORE `runTtp()` (which does its own
 *  internal slice-time-correction -> Gd -> TTP), i.e. MC-then-STC rather than
 *  the real STC-then-MC — splitting `runTtp()`'s bundled internal
 *  slice-time-correction step out to match the real order exactly was out of
 *  scope for this pass. This is a known, deliberate simplification, not an
 *  oversight: for the small, sub-voxel motion typical between adjacent PWI
 *  frames, the two orderings are a second-order difference, and getting real
 *  motion correction into the pipeline at all (previously entirely absent)
 *  is the load-bearing fix. The brain mask is still derived from frame 0
 *  alone (not the motion-corrected temporal mean) — a second, separate,
 *  still-open simplification, also out of this pass's scope. */
export async function runPwiTtp(
  pwi4dFile: File | Blob,
  subjectId: string,
  sidecarFile: File | Blob | null,
  onProgress: (stage: string) => void,
): Promise<PwiTtpResult> {
  const t0 = performance.now();
  const { loadNiftiFile4D } = await import("./nifti");
  onProgress("loading raw 4D PWI series");
  const pwi4dRaw = await loadNiftiFile4D(pwi4dFile);

  let sidecar: PwiJsonSidecar | null = null;
  if (sidecarFile) {
    try {
      sidecar = JSON.parse(await (sidecarFile as Blob).text());
    } catch {
      sidecar = null;
    }
  }
  const tr = resolveRepetitionTime(pwi4dRaw, sidecar);
  const sliceTiming = resolveSliceTiming(subjectId, pwi4dRaw.dims[2], tr, sidecar);

  onProgress(`motion correction: ${pwi4dRaw.dims[3] - 1} frames to register`);
  const pwi4d = await motionCorrectPwi4d(pwi4dRaw, onProgress);

  onProgress("deriving brain mask for this series via SynthStrip on frame 0");
  const frame0 = extractFrame3D(pwi4d, 0);
  const worker = new Worker(appPath("workers/synthstrip.worker.js"), { type: "module" });
  let maskResult: { mask: ArrayBuffer; dims: number[]; brainVoxels: number; totalMs: number };
  try {
    maskResult = await runWorker(
      worker,
      { voxels: ab(frame0.voxels), dims: frame0.dims, pixdim: frame0.pixdim, affine: ab(frame0.affine) },
      (s) => onProgress(`synthstrip(ttp-mask): ${s}`),
    );
  } finally {
    worker.terminate();
  }
  const pwiMask = new Uint8Array(maskResult.mask);
  const pwiMaskRef: NiftiVolume = { dims: frame0.dims, pixdim: frame0.pixdim, affine: frame0.affine, voxels: Float32Array.from(pwiMask), datatype: 16 };

  onProgress("slice-time correction -> Gd concentration -> per-voxel peak detection");
  const ttpDims4: Dims4 = pwi4d.dims;
  const result = await runTtp(
    { pwi: pwi4d.voxels, dims: ttpDims4, pixdim: pwi4d.pixdim, mask: pwiMask, sliceTiming },
    (s) => onProgress(`ttp: ${s}`),
  );

  return {
    ttpMap: result.ttp,
    ttpDims: result.dims,
    ttpAffine: pwi4d.affine,
    ttpPixdim: [pwi4d.pixdim[0], pwi4d.pixdim[1], pwi4d.pixdim[2]],
    pwiMask,
    pwiMaskRef,
    totalMs: Math.round(performance.now() - t0),
  };
}

// --------------------------------------------------------------- registration ---

export interface PwiRegistrationResult {
  /** TTP in DWI's MNI-affine space, un-normalized. */
  ttpAff: NiftiVolume;
  /** desc-norm'd (percentile-clip[1,99]-rescale then z-score-in-mask) channels, affine-only + desc-norm space — the space PWI's DAGMNet model actually consumes (real find_files_path priority, not DWI's own Affine+SyN space). */
  dwiDescNorm: NiftiVolume;
  adcDescNorm: NiftiVolume;
  ttpDescNorm: NiftiVolume;
  /** Step 1's PWI-native-mask -> DWI-native-mask affine (`/out_affine_0GenericAffine.mat`
   *  bytes). Exposed so `restorePwiHpToNativeSpace` can invert it — this is
   *  the same role as the real pipeline's separately-named
   *  `invaff_space-ADC2individualTTP.mat`, just computed once and reused
   *  rather than stored under a second name. */
  coregAffine: ArrayBuffer;
  /** Raw TTP resampled onto DWI's native grid (native `_TTP_space-DWI`), and the
   *  same masked by DWI's native brain mask (`_TTP_space-DWI_desc-brain`). */
  ttpOnDwiNative?: NiftiVolume;
  ttpOnDwiNativeBrain?: NiftiVolume;
}

/**
 * Real PWI registration (`registration_pwi_align.py`'s two-step sequence):
 *   Step 1: Affine (fixed = DWI's NATIVE pre-MNI brain mask, moving = PWI's
 *     own native mask) -> antsApplyTransforms that onto raw TTP -> mask with
 *     DWI's native brain mask.
 *   Step 2: antsApplyTransforms DWI's own native->MNI affine transform
 *     (reused, not recomputed) onto the masked TTP.
 *   Then desc-norm (percentile-clip[1,99]-rescale then z-score-in-mask) for
 *   DWI/ADC (DWI's own *affine-only* output, not SyN) and TTP.
 *
 * `dwiNative` is DWI's native prep volume+mask (same object `engine.ts`'s
 * skull-strip stage already produced); `dwiReg` is DWI's registration result
 * (same object `runDwiRegistration()` already produced) — both are DWI
 * prerequisites this function does not compute itself.
 */
export async function runPwiRegistration(
  ttp: PwiTtpResult,
  dwiNative: { vol: NiftiVolume; mask: Uint8Array },
  dwiReg: DwiRegistrationResult,
  onProgress: (stage: string) => void,
): Promise<PwiRegistrationResult> {
  const dwiNativeMaskVol: NiftiVolume = {
    dims: dwiNative.vol.dims,
    pixdim: dwiNative.vol.pixdim,
    affine: dwiNative.vol.affine,
    voxels: Float32Array.from(dwiNative.mask),
    datatype: 16,
  };

  onProgress("step 1/2: affine (fixed = DWI native brain mask, moving = PWI native mask)");
  const coregResult = await runAntsRegistration(
    {
      step: "affine",
      voxels: ttp.pwiMaskRef.voxels,
      dims: ttp.pwiMaskRef.dims,
      pixdim: ttp.pwiMaskRef.pixdim,
      affine: ttp.pwiMaskRef.affine,
      fixedOverride: { voxels: dwiNativeMaskVol.voxels, dims: dwiNativeMaskVol.dims, pixdim: dwiNativeMaskVol.pixdim, affine: dwiNativeMaskVol.affine },
    },
    (s) => onProgress(`coreg-affine: ${s}`),
  );
  const coregMat = coregResult.transformFiles["/out_affine_0GenericAffine.mat"];
  if (!coregMat) throw new Error("PWI Registration: PWI->DWI coregistration transform missing from worker output");

  onProgress("applying PWI->DWI-native transform to raw TTP");
  const ttpOnDwiNativeResult = await runAntsApplyTransform(
    {
      voxels: ttp.ttpMap,
      dims: ttp.ttpDims,
      pixdim: ttp.ttpPixdim,
      affine: ttp.ttpAffine,
      transforms: [{ path: "/coreg_aff.mat", bytes: coregMat }],
      fixedTemplate: "affine",
      fixedOverride: { voxels: dwiNativeMaskVol.voxels, dims: dwiNativeMaskVol.dims, pixdim: dwiNativeMaskVol.pixdim, affine: dwiNativeMaskVol.affine },
    },
    (s) => onProgress(`apply-coreg-ttp: ${s}`),
  );
  const ttpOnDwiNative: NiftiVolume = { dims: ttpOnDwiNativeResult.dims, pixdim: ttpOnDwiNativeResult.pixdim, affine: ttpOnDwiNativeResult.affine, voxels: ttpOnDwiNativeResult.registered, datatype: 16 };

  // Brain-mask with DWI's native mask (registration_pwi_align.py: apply_brain_mask_numpy — plain elementwise zero-outside-mask).
  const ttpMaskedVoxels = new Float32Array(ttpOnDwiNative.voxels.length);
  for (let i = 0; i < ttpMaskedVoxels.length; i++) ttpMaskedVoxels[i] = dwiNative.mask[i] ? ttpOnDwiNative.voxels[i] : 0;
  const ttpMasked: NiftiVolume = { ...ttpOnDwiNative, voxels: ttpMaskedVoxels };

  onProgress("step 2/2: applying DWI's own native->MNI affine transform to masked TTP (reused, not recomputed)");
  const ttpAffResult = await runAntsApplyTransform(
    {
      voxels: ttpMasked.voxels,
      dims: ttpMasked.dims,
      pixdim: ttpMasked.pixdim,
      affine: ttpMasked.affine,
      transforms: [{ path: "/dwi_aff.mat", bytes: dwiReg.dwiAffineTransform }],
      fixedTemplate: "affine",
    },
    (s) => onProgress(`apply-dwi-affine-ttp: ${s}`),
  );
  const ttpAff: NiftiVolume = { dims: ttpAffResult.dims, pixdim: ttpAffResult.pixdim, affine: ttpAffResult.affine, voxels: ttpAffResult.registered, datatype: 16 };

  // desc-norm, using DWI's affine-registered (MNI-space) mask as the
  // normalization mask — the subject's own mask IS a registration of the
  // real code's dwi_mask_template, so its extent is very close.
  const affMaskBin = new Uint8Array(dwiReg.affineMask.voxels.length);
  for (let i = 0; i < affMaskBin.length; i++) affMaskBin[i] = dwiReg.affineMask.voxels[i] > 0.5 ? 1 : 0;
  const descNorm = (vol: NiftiVolume): NiftiVolume => ({ ...vol, voxels: zscoreInMask(percentileClipRescaleInMask(vol.voxels, affMaskBin), affMaskBin) });

  onProgress("desc-norm: percentile-clip[1,99]-rescale then z-score-in-mask for DWI/ADC/TTP");
  return {
    ttpAff,
    dwiDescNorm: descNorm(dwiReg.affineDwi),
    adcDescNorm: descNorm(dwiReg.affineAdc),
    ttpDescNorm: descNorm(ttpAff),
    coregAffine: coregMat,
    ttpOnDwiNative,
    ttpOnDwiNativeBrain: ttpMasked,
  };
}

// ----------------------------------------------------------- segmentation ---

export interface PwiSegmentationResult {
  mask: Uint8Array;
  dims: Dims;
  lesionVoxels: number;
  totalMs: number;
}

/**
 * Real DAGMNet PWI (ch4) ONNX model — DWI/ADC/TTP (affine+desc-norm space)
 * in, mask out. The 4th channel (HPsymclassic, hemispheric-symmetry SLIC
 * features) is **deliberately zero-filled, not computed**: a real SLIC-based
 * generator exists (`hypoperfusion.ts`'s `computeHypoperfusionSymClassic`)
 * and runs, but feeding its real output to this model was live-tested and
 * *regressed* Dice from 0.7143 (zero-filled) to 0.6263 — the model is
 * sensitive to this channel's exact values in a way the hand-ported TS SLIC
 * (Dice=0.8395 vs the real reference file, not bit-exact skimage parity)
 * doesn't satisfy. Do not re-enable without a real fix to the SLIC step
 * itself first (verified against the model, not just against the reference
 * mask in isolation).
 */
export async function runPwiSegmentation(
  reg: PwiRegistrationResult,
  onProgress: (stage: string) => void,
): Promise<PwiSegmentationResult> {
  const symclassic = new Float32Array(reg.dwiDescNorm.voxels.length); // zero-filled, see header comment
  const worker = new Worker(appPath("workers/dagmnet-pwi.worker.js"), { type: "module" });
  try {
    const result = await runWorker<
      { dwi: ArrayBuffer; adc: ArrayBuffer; ttp: ArrayBuffer; symclassic: ArrayBuffer; dims: Dims; affine: ArrayBuffer },
      { mask: ArrayBuffer; dims: Dims; lesionVoxels: number; totalMs: number }
    >(
      worker,
      {
        dwi: ab(reg.dwiDescNorm.voxels),
        adc: ab(reg.adcDescNorm.voxels),
        ttp: ab(reg.ttpDescNorm.voxels),
        symclassic: ab(symclassic),
        dims: reg.dwiDescNorm.dims,
        affine: ab(reg.dwiDescNorm.affine),
      },
      (s) => onProgress(`dagmnet-pwi: ${s}`),
    );
    return { mask: new Uint8Array(result.mask), dims: result.dims, lesionVoxels: result.lesionVoxels, totalMs: result.totalMs };
  } finally {
    worker.terminate();
  }
}

// ---------------------------------------------------- native-space restore ---

export interface PwiNativeSpaceResult {
  mask: Uint8Array;
  dims: Dims;
  pixdim: Dims;
  affine: Float32Array;
}

/**
 * Real `restore_hp_mni2orig` (`ads/services/segmentation/pwi_hp_mni2orig.py`):
 * warps the MNI-affine hypoperfusion mask back to the subject's own native
 * space, in two steps, both by **inverting an already-computed forward
 * transform** (ANTsPy `whichtoinvert=[True]`, this app's WASM equivalent is
 * `antsApplyTransforms`' `-t [path,1]` bracket syntax) rather than solving a
 * new registration:
 *   Step 1: MNI-affine -> DWI/ADC-native, inverting DWI's own
 *     individual->MNI affine transform (`dwiReg.dwiAffineTransform`).
 *   Step 2 (real code's "preferred" path, always taken here since this app
 *     always has both inputs): ADC-native -> PWI's own native TTP space,
 *     inverting the PWI->DWI coregistration affine computed in
 *     `runPwiRegistration` Step 1 (`pwiReg.coregAffine`) — this is the same
 *     role as the real pipeline's separately-named
 *     `invaff_space-ADC2individualTTP.mat`, just inverted on the fly from
 *     the one forward transform this app already computed, instead of being
 *     stored under a second name.
 * Both steps use nearestNeighbor (label data). Final result is masked with
 * the PWI series' own native brain mask (`ttp.pwiMask`, same grid as the
 * Step 2 output by construction — both derived from the 4D PWI series'
 * frame 0), matching the real code's own masking step.
 */
export async function restorePwiHpToNativeSpace(
  hpMni: PwiSegmentationResult,
  dwiReg: DwiRegistrationResult,
  pwiReg: PwiRegistrationResult,
  dwiNative: { vol: NiftiVolume; mask: Uint8Array },
  ttp: PwiTtpResult,
  onProgress: (stage: string) => void,
): Promise<PwiNativeSpaceResult> {
  const dwiNativeMaskVol: NiftiVolume = {
    dims: dwiNative.vol.dims,
    pixdim: dwiNative.vol.pixdim,
    affine: dwiNative.vol.affine,
    voxels: Float32Array.from(dwiNative.mask),
    datatype: 16,
  };

  onProgress("restore-native: step 1/2 (MNI-affine -> DWI/ADC-native, inverting DWI's individual->MNI affine)");
  const toAdcNative = await runAntsApplyTransform(
    {
      voxels: Float32Array.from(hpMni.mask),
      dims: hpMni.dims,
      pixdim: pwiReg.dwiDescNorm.pixdim,
      affine: pwiReg.dwiDescNorm.affine,
      transforms: [{ path: "/dwi_aff.mat", bytes: dwiReg.dwiAffineTransform, invert: true }],
      fixedTemplate: "affine",
      fixedOverride: { voxels: dwiNativeMaskVol.voxels, dims: dwiNativeMaskVol.dims, pixdim: dwiNativeMaskVol.pixdim, affine: dwiNativeMaskVol.affine },
      interpolator: "nearestNeighbor",
    },
    (s) => onProgress(`restore-native(mni2adc): ${s}`),
  );

  onProgress("restore-native: step 2/2 (ADC-native -> PWI native TTP space, inverting the PWI->DWI coregistration affine)");
  const toTtpNative = await runAntsApplyTransform(
    {
      voxels: toAdcNative.registered,
      dims: toAdcNative.dims,
      pixdim: toAdcNative.pixdim,
      affine: toAdcNative.affine,
      transforms: [{ path: "/coreg_aff.mat", bytes: pwiReg.coregAffine, invert: true }],
      fixedTemplate: "affine",
      fixedOverride: { voxels: ttp.pwiMaskRef.voxels, dims: ttp.pwiMaskRef.dims, pixdim: ttp.pwiMaskRef.pixdim, affine: ttp.pwiMaskRef.affine },
      interpolator: "nearestNeighbor",
    },
    (s) => onProgress(`restore-native(adc2ttp): ${s}`),
  );

  const mask = new Uint8Array(toTtpNative.registered.length);
  for (let i = 0; i < mask.length; i++) {
    mask[i] = toTtpNative.registered[i] > 0.5 && ttp.pwiMask[i] > 0.5 ? 1 : 0;
  }
  onProgress(`restore-native: done — ${mask.reduce((a, b) => a + b, 0).toLocaleString()} voxels in native space`);

  return { mask, dims: toTtpNative.dims, pixdim: toTtpNative.pixdim, affine: toTtpNative.affine };
}
